/**
 * <s-infinite-scroll> — Appends the next page of a paginated product grid as the
 * shopper nears the end of the list.
 *
 * Progressive enhancement: the server renders normal numbered pagination
 * ([data-pagination]); once this element connects, that nav is hidden and pages
 * load in place. Filters and sort are preserved because the next URL comes
 * straight from Shopify's paginate object (it carries the current query string).
 *
 * Attributes:
 *   data-next-url        — URL of the next page; empty when on the last page.
 *   data-loaded-message  — Live-region text; "__count__" is replaced.
 *   data-error-message   — Shown on the retry button's status when a fetch fails.
 * Child markup:
 *   [data-grid]          — The list that receives new items (matched by id in fetched pages).
 *   [data-sentinel]      — Observed; entering the viewport triggers a load.
 *   [data-load-more]     — Button fallback / retry; hidden while auto-loading works.
 *   [data-status]        — Polite live region for screen readers.
 *   [data-progress]      — Text "Showing X of Y", updated after each load.
 *   [data-pagination]    — Server pagination, hidden once enhanced.
 */
class SInfiniteScroll extends HTMLElement {
  #ac;
  #observer;
  #busy = false;
  #shown = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const grid = this.#grid();
    this.#shown = grid ? grid.children.length : 0;

    this.querySelector('[data-pagination]')?.setAttribute('hidden', '');
    const button = this.querySelector('[data-load-more]');
    button?.addEventListener('click', () => this.#load(), { signal: this.#ac.signal });

    if (!this.dataset.nextUrl) {
      this.#finish();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.#load();
        },
        { rootMargin: '0px 0px 900px 0px' },
      );
      const sentinel = this.querySelector('[data-sentinel]');
      if (sentinel) this.#observer.observe(sentinel);
    } else {
      button?.removeAttribute('hidden');
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #grid() {
    return this.querySelector('[data-grid]');
  }

  async #load() {
    const url = this.dataset.nextUrl;
    if (this.#busy || !url) return;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    const button = this.querySelector('[data-load-more]');
    button?.setAttribute('hidden', '');

    try {
      const response = await fetch(url, { signal: this.#ac.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = doc.querySelector('s-infinite-scroll');
      const grid = this.#grid();
      const incoming = nextRoot?.querySelector('[data-grid]');
      if (!grid || !incoming) throw new Error('Grid not found');

      const items = Array.from(incoming.children);
      const fragment = document.createDocumentFragment();
      for (const item of items) fragment.append(document.adoptNode(item));
      grid.append(fragment);
      this.#shown += items.length;

      this.dataset.nextUrl = nextRoot.dataset.nextUrl || '';
      this.#updateProgress();
      this.#announce(items.length);

      if (!this.dataset.nextUrl) this.#finish();
    } catch (error) {
      if (error.name === 'AbortError') return;
      button?.removeAttribute('hidden');
      const status = this.querySelector('[data-status]');
      if (status && this.dataset.errorMessage) status.textContent = this.dataset.errorMessage;
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }

    // If the new rows didn't push the sentinel out of view, keep going.
    const sentinel = this.querySelector('[data-sentinel]');
    if (this.dataset.nextUrl && sentinel && sentinel.getBoundingClientRect().top < window.innerHeight + 900) {
      requestAnimationFrame(() => this.#load());
    }
  }

  #updateProgress() {
    const progress = this.querySelector('[data-progress]');
    if (!progress) return;
    const template = progress.dataset.template || '';
    const total = progress.dataset.total || '';
    progress.textContent = template.replace('__shown__', String(this.#shown)).replace('__total__', total);
  }

  #announce(count) {
    const status = this.querySelector('[data-status]');
    const message = this.dataset.loadedMessage;
    if (status && message) status.textContent = message.replace('__count__', String(count));
  }

  #finish() {
    this.#observer?.disconnect();
    this.querySelector('[data-load-more]')?.setAttribute('hidden', '');
    this.querySelector('[data-loading]')?.setAttribute('hidden', '');
  }
}

if (!customElements.get('s-infinite-scroll')) customElements.define('s-infinite-scroll', SInfiniteScroll);
